import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Download,
} from "lucide-react";
import { http, rupiah, apiError } from "@/lib/api";
import AdminShell, { PageHead } from "@/components/AdminShell";

const FLOW = ["new", "preparing", "ready", "completed"];

const STATUS = {
  new: "Baru",
  preparing: "Diproses",
  ready: "Siap",
  completed: "Selesai",
  cancelled: "Batal",
};

const PAY = {
  unpaid: "Belum Bayar",
  pending: "Menunggu",
  paid: "Lunas",
  failed: "Gagal",
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [tableFilter, setTableFilter] = useState("all");
  const [exporting, setExporting] = useState(false);

  const [size, setSize] = useState(
    () => Number(localStorage.getItem("wm_order_size")) || 10
  );

  const [page, setPage] = useState(1);

  // =========================================================
  // GLOBAL SOUND + PUSH
  // =========================================================
  // Suara dan Web Push sekarang dikelola oleh AdminShell.
  // Karena itu fitur tetap aktif saat membuka Dashboard,
  // Produk, Kategori, Modifier, Meja, Tema, Pembayaran,
  // Laporan, Pengaturan, maupun halaman Pesanan.
  //
  // Orders.jsx hanya menangani data dan aksi pesanan agar
  // tidak terjadi double polling suara / double beep.
  // =========================================================

  // =========================================================
  // LOAD ORDERS
  // =========================================================

  async function load() {
    try {
      const { data } = await http.get("/admin/orders");
      setOrders(data);
    } catch (e) {
      console.error("[ORDERS] load error:", e);
    }
  }

  useEffect(() => {
    load();

    const timer = setInterval(load, 7000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  // =========================================================
  // ORDER ACTIONS
  // =========================================================

  function changeSize(v) {
    setSize(v);

    localStorage.setItem(
      "wm_order_size",
      String(v)
    );
  }

  async function setStatus(
    id,
    status
  ) {
    const ord =
      orders.find(
        (o) => o.id === id
      );

    if (
      status === "completed" &&
      ord &&
      ord.payment_status !==
        "paid"
    ) {
      toast.error(
        "Pesanan belum lunas. Tandai pembayaran LUNAS dulu sebelum menyelesaikan."
      );

      return;
    }

    try {
      await http.patch(
        `/admin/orders/${id}/status`,
        { status }
      );

      setOrders(
        (current) =>
          current.map(
            (o) =>
              o.id === id
                ? {
                    ...o,
                    status,
                  }
                : o
          )
      );

      toast.success(
        "Status diperbarui"
      );
    } catch (e) {
      toast.error(
        apiError(e)
      );
    }
  }

  async function markPaid(id) {
    try {
      await http.patch(
        `/admin/orders/${id}/cash-paid`,
        { paid: true }
      );

      setOrders(
        (current) =>
          current.map(
            (o) =>
              o.id === id
                ? {
                    ...o,
                    payment_status:
                      "paid",
                  }
                : o
          )
      );

      toast.success(
        "Ditandai lunas"
      );
    } catch (e) {
      toast.error(
        apiError(e)
      );
    }
  }

  async function downloadExcel() {
    if (exporting) return;

    setExporting(true);

    try {
      const params = new URLSearchParams();

      if (tableFilter !== "all") {
        params.set("table_name", tableFilter);
      }

      if (q.trim()) {
        params.set("q", q.trim());
      }

      const queryString = params.toString();

      const response = await http.get(
        `/admin/orders/export${queryString ? `?${queryString}` : ""}`,
        {
          responseType: "blob",
        }
      );

      const blob = new Blob(
        [response.data],
        {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }
      );

      const url =
        window.URL.createObjectURL(
          blob
        );

      const link =
        document.createElement(
          "a"
        );

      link.href = url;

      const date =
        new Date()
          .toISOString()
          .slice(0, 10);

      const tableSuffix =
        tableFilter === "all"
          ? "semua-meja"
          : tableFilter
              .toLowerCase()
              .replace(
                /[^a-z0-9]+/g,
                "-"
              )
              .replace(
                /^-|-$/g,
                ""
              );

      link.download =
        `pesanan-${tableSuffix}-${date}.xlsx`;

      document.body.appendChild(
        link
      );

      link.click();
      link.remove();

      window.URL.revokeObjectURL(
        url
      );

      toast.success(
        "Excel pesanan berhasil diunduh"
      );
    } catch (e) {
      toast.error(
        apiError(e)
      );
    } finally {
      setExporting(false);
    }
  }

  // =========================================================
  // FILTER + PAGINATION
  // =========================================================

  const tableOptions =
    useMemo(() => {
      const names = Array.from(
        new Set(
          orders
            .map((o) =>
              String(
                o.table_name || ""
              ).trim()
            )
            .filter(Boolean)
        )
      );

      return names.sort(
        (a, b) =>
          a.localeCompare(
            b,
            "id",
            {
              numeric: true,
              sensitivity: "base",
            }
          )
      );
    }, [orders]);

  const filtered =
    useMemo(() => {
      const s =
        q
          .trim()
          .toLowerCase();

      return orders.filter(
        (o) => {
          const matchesSearch =
            !s ||
            o.id
              .toLowerCase()
              .includes(s) ||
            (
              o.customer_name ||
              ""
            )
              .toLowerCase()
              .includes(s) ||
            (
              o.customer_phone ||
              ""
            ).includes(s);

          const matchesTable =
            tableFilter === "all" ||
            String(
              o.table_name || ""
            ) ===
              tableFilter;

          return (
            matchesSearch &&
            matchesTable
          );
        }
      );
    }, [orders, q, tableFilter]);

  const totalPages =
    Math.max(
      1,
      Math.ceil(
        filtered.length /
          size
      )
    );

  const pageItems =
    filtered.slice(
      (page - 1) * size,
      page * size
    );

  useEffect(() => {
    setPage(1);
  }, [q, size, tableFilter]);

  // =========================================================
  // UI
  // =========================================================

  return (
    <AdminShell>
      <PageHead
        eyebrow="Operasional"
        title="Pesanan"
        desc="Kelola & perbarui status pesanan (maju bertahap, tidak bisa mundur)."
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex flex-1 items-center gap-2 rounded-xl border border-border bg-card px-3">
          <Search
            size={16}
            className="text-muted-foreground"
          />

          <input
            data-testid="order-search"
            value={q}
            onChange={(e) =>
              setQ(
                e.target.value
              )
            }
            placeholder="Cari ID / nama / no. HP"
            className="w-full bg-transparent py-2.5 text-sm outline-none"
          />
        </div>

        <select
          data-testid="table-filter"
          value={tableFilter}
          onChange={(e) =>
            setTableFilter(
              e.target.value
            )
          }
          className="rounded-xl border border-border bg-card px-3 py-2.5 text-sm font-semibold outline-none"
        >
          <option value="all">
            Semua Meja
          </option>

          {tableOptions.map(
            (tableName) => (
              <option
                key={tableName}
                value={tableName}
              >
                {tableName}
              </option>
            )
          )}
        </select>

        <button
          type="button"
          data-testid="download-orders-excel"
          onClick={downloadExcel}
          disabled={exporting || filtered.length === 0}
          className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5 text-sm font-bold text-card-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Download
            size={16}
          />
          {exporting
            ? "Membuat Excel..."
            : "Unduh Excel"}
        </button>

        <select
          data-testid="page-size"
          value={size}
          onChange={(e) =>
            changeSize(
              Number(
                e.target.value
              )
            )
          }
          className="rounded-xl border border-border bg-card px-3 py-2.5 text-sm font-semibold outline-none"
        >
          {[10, 20, 50, 70].map(
            (n) => (
              <option
                key={n}
                value={n}
              >
                {`${n} / halaman`}
              </option>
            )
          )}
        </select>
      </div>

      <div className="space-y-3">
        {pageItems.map(
          (o) => {
            const curIdx =
              FLOW.indexOf(
                o.status
              );

            const final =
              o.status ===
                "completed" ||
              o.status ===
                "cancelled";

            return (
              <div
                key={o.id}
                data-testid={`order-${o.id}`}
                className="rounded-3xl border border-border bg-card p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-heading text-sm font-bold text-card-foreground">
                      {o.id}
                    </div>

                    <div className="text-xs text-muted-foreground">
                      {
                        o.customer_name
                      }{" "}
                      ·{" "}
                      {
                        o.customer_phone
                      }{" "}
                      ·{" "}
                      {
                        o.table_name
                      }
                    </div>
                  </div>

                  <b className="font-heading text-lg font-extrabold text-primary">
                    {rupiah(
                      o.total
                    )}
                  </b>
                </div>

                <div className="mt-2 space-y-1 rounded-xl bg-muted/50 p-2 text-xs">
                  {o.items.map(
                    (i, x) => {
                      const base =
                        i.unit_price -
                        (
                          i.modifiers ||
                          []
                        ).reduce(
                          (
                            sum,
                            m
                          ) =>
                            sum +
                            (m.price_delta ||
                              0) *
                              m.qty,
                          0
                        );

                      return (
                        <div
                          key={
                            x
                          }
                        >
                          <div className="flex justify-between text-card-foreground">
                            <span>
                              {
                                i.qty
                              }
                              ×{" "}
                              {
                                i.name
                              }
                            </span>

                            <span>
                              {rupiah(
                                base *
                                  i.qty
                              )}
                            </span>
                          </div>

                          {(
                            i.modifiers ||
                            []
                          ).map(
                            (
                              m,
                              mi
                            ) => (
                              <div
                                key={
                                  mi
                                }
                                className="flex justify-between pl-3 text-[11px] text-muted-foreground"
                              >
                                <span>
                                  +{" "}
                                  {
                                    m.name
                                  }
                                  ×
                                  {
                                    m.qty
                                  }
                                </span>

                                <span>
                                  {m.price_delta
                                    ? rupiah(
                                        m.price_delta *
                                          m.qty
                                      )
                                    : "-"}
                                </span>
                              </div>
                            )
                          )}

                          {i.note && (
                            <div className="pl-3 text-[11px] italic text-muted-foreground">
                              "
                              {
                                i.note
                              }
                              "
                            </div>
                          )}
                        </div>
                      );
                    }
                  )}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-primary/15 px-2.5 py-1 text-[10px] font-bold uppercase text-primary">
                    {
                      o.payment_method
                    }
                  </span>

                  <span
                    className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${
                      o.payment_status ===
                      "paid"
                        ? "bg-emerald-500/15 text-emerald-500"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {
                      PAY[
                        o
                          .payment_status
                      ]
                    }
                  </span>

                  {o.payment_method ===
                    "cash" &&
                    o.payment_status !==
                      "paid" && (
                      <button
                        data-testid={`mark-paid-${o.id}`}
                        onClick={() =>
                          markPaid(
                            o.id
                          )
                        }
                        className="rounded-full bg-primary px-3 py-1 text-[10px] font-bold text-primary-foreground"
                      >
                        Tandai
                        Lunas
                      </button>
                    )}

                  <div className="ml-auto flex gap-1">
                    {FLOW.map(
                      (s, i) => {
                        const active =
                          o.status ===
                          s;

                        const needsPay =
                          s ===
                            "completed" &&
                          o.payment_status !==
                            "paid";

                        const locked =
                          final ||
                          i <
                            curIdx;

                        const disabled =
                          (locked &&
                            !active) ||
                          needsPay;

                        return (
                          <button
                            key={
                              s
                            }
                            data-testid={`status-${o.id}-${s}`}
                            disabled={
                              disabled
                            }
                            title={
                              needsPay
                                ? "Tandai LUNAS dulu untuk menyelesaikan"
                                : ""
                            }
                            onClick={() =>
                              setStatus(
                                o.id,
                                s
                              )
                            }
                            className={`rounded-lg px-2.5 py-1.5 text-[10px] font-bold transition-colors ${
                              active
                                ? "bg-primary text-primary-foreground"
                                : disabled
                                ? "cursor-not-allowed bg-muted/50 text-muted-foreground/40"
                                : "bg-muted text-muted-foreground hover:bg-primary/20"
                            }`}
                          >
                            {
                              STATUS[
                                s
                              ]
                            }
                          </button>
                        );
                      }
                    )}
                  </div>
                </div>
              </div>
            );
          }
        )}

        {filtered.length ===
          0 && (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            Tidak ada pesanan.
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-5 flex items-center justify-center gap-3">
          <button
            disabled={
              page <= 1
            }
            onClick={() =>
              setPage(
                page - 1
              )
            }
            className="grid h-9 w-9 place-items-center rounded-xl bg-card disabled:opacity-40"
          >
            <ChevronLeft
              size={16}
            />
          </button>

          <span
            data-testid="page-indicator"
            className="text-sm font-semibold text-muted-foreground"
          >
            {page} /{" "}
            {totalPages}
          </span>

          <button
            disabled={
              page >=
              totalPages
            }
            onClick={() =>
              setPage(
                page + 1
              )
            }
            className="grid h-9 w-9 place-items-center rounded-xl bg-card disabled:opacity-40"
          >
            <ChevronRight
              size={16}
            />
          </button>
        </div>
      )}
    </AdminShell>
  );
}
